import { BaseStore } from "./BaseStore";
import { type Getter, atom } from "jotai";
import { loadable } from "jotai/utils";

export interface AsyncStoreOptions<T> {
  onSuccess?: (data: T) => void;
  onError?: (error: any) => void;
}

/**
 * @example 
 *
 * ```ts
  const accountAsyncStore = new AsyncStore(
    async (args: { accountUid: string }) => {
      const resp = await getOrgAccount({
        accountUid: args.accountUid,
      });
      if (resp.error) {
        throw resp.error;
      }

      return account;
    },
    {
      onSuccess: (data) => {
        console.info("getOrgAccount", data);
      },
      onError: (error) => {
        console.error("getOrgAccount", error);
      }
    }
  );
  ```
 */
export class AsyncStore<Args, T> extends BaseStore {
  loadId = atom<number | undefined>(undefined);
  args = atom<Args | undefined>(undefined);

  asyncAtom = atom(async (get) => {
    const loadId = get(this.loadId);
    if (loadId === undefined) return;

    // use this.get avoid re-run asyncAtom when args changed
    const args = this.get(this.args);

    try {
      const data = await this.asyncFn(args as Args, get);

      this._callback(loadId, () => {
        this.options.onSuccess?.(data);
      });

      return data;
    } catch (error) {
      this._callback(loadId, () => {
        this.options.onError?.(error);
      });
      throw error;
    }
  });

  loadableAtom = loadable(this.asyncAtom);

  data = atom((get) => {
    const loadableAtom = get(this.loadableAtom);
    return loadableAtom.state === "hasData" ? loadableAtom.data : undefined;
  });

  loading = atom((get) => {
    const loadableAtom = get(this.loadableAtom);
    return loadableAtom.state === "loading";
  });

  error = atom((get) => {
    const loadableAtom = get(this.loadableAtom);
    return loadableAtom.state === "hasError" ? loadableAtom.error : undefined;
  });

  constructor(
    private readonly asyncFn: (args: Args, get: Getter) => Promise<T>,
    private readonly options: AsyncStoreOptions<T> = {}
  ) {
    super();
  }

  load(args: Args) {
    this.set(this.args, args);
    this.set(this.loadId, (c) => (c ?? 0) + 1);
  }

  reset() {
    this.set(this.loadId, undefined);
    this.set(this.args, undefined);
  }

  private _callback(loadId: number, fn: () => void) {
    const currentLoadId = this.get(this.loadId);
    if (currentLoadId === loadId) {
      fn();
    }
  }
}
